/**
 * Trust-page layout atoms.
 *
 * Used by /about, /how-it-works, /why-feelgreats, /mentor-support,
 * /science, /stories, /faq, and the upgraded /contact page.
 *
 * Design language:  Apple × premium wellness × calm concierge.
 *   - heavy whitespace
 *   - left-aligned reading rhythm
 *   - emerald/amber accent system (no purple gradients)
 *   - thin display headings + uppercase tracked eyebrows
 *   - all interactive elements have data-testids
 */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, HeartHandshake, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import TrustBadgeRow from "./TrustBadgeRow";

// ----------------------------------------------------------------------------
// Page shell — gradient background + page-level container
// ----------------------------------------------------------------------------
export function TrustPage({ children, testid, className = "" }) {
  return (
    <main
      className={`bg-gradient-to-b from-emerald-50/30 via-white to-white overflow-x-hidden ${className}`}
      data-testid={testid}
    >
      {children}
    </main>
  );
}

// ----------------------------------------------------------------------------
// Hero — uppercase eyebrow + thin display headline + body
// ----------------------------------------------------------------------------
export function TrustHero({
  eyebrow,
  headline,
  highlightWord,
  body,
  primaryCta,
  secondaryCta,
  badgeRow = true,
  testid,
}) {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 sm:pt-20 sm:pb-16" data-testid={testid}>
      {eyebrow && (
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700 bg-emerald-50/60 rounded-full px-3 py-1 border border-emerald-100">
          <Sparkles className="w-3 h-3" /> {eyebrow}
        </span>
      )}
      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-emerald-950 leading-[1.05] max-w-3xl mt-5"
      >
        {renderHighlighted(headline, highlightWord)}
      </motion.h1>
      {body && (
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-5 text-lg text-emerald-900/75 max-w-2xl leading-relaxed"
        >
          {body}
        </motion.p>
      )}
      {(primaryCta || secondaryCta) && (
        <div className="mt-7 flex flex-wrap gap-3">
          {primaryCta && <CtaButton {...primaryCta} primary />}
          {secondaryCta && <CtaButton {...secondaryCta} />}
        </div>
      )}
      {badgeRow && (
        <div className="mt-9 max-w-2xl">
          <TrustBadgeRow compact />
        </div>
      )}
    </section>
  );
}

function renderHighlighted(text, highlight) {
  if (!highlight || !text || !text.includes(highlight)) return text;
  const parts = text.split(highlight);
  return (
    <>
      {parts[0]}
      <em className="italic text-emerald-700">{highlight}</em>
      {parts.slice(1).join(highlight)}
    </>
  );
}

// ----------------------------------------------------------------------------
// CTA Button — internal Link or external <a>
// ----------------------------------------------------------------------------
export function CtaButton({ to, href, children, label, primary, external, testid, icon: Icon }) {
  const content = (
    <Button
      className={
        primary
          ? "cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 py-6 text-base"
          : "rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-7 py-6 text-base"
      }
      variant={primary ? "default" : "outline"}
      data-testid={testid}
    >
      {Icon && <Icon className="w-4 h-4 mr-1.5" />}
      {children || label}
      {primary && !Icon && <ArrowRight className="w-4 h-4 ml-1.5" />}
    </Button>
  );
  if (to) return <Link to={to}>{content}</Link>;
  if (href) {
    return external ? (
      <a href={href} target="_blank" rel="noopener noreferrer">{content}</a>
    ) : (
      <a href={href}>{content}</a>
    );
  }
  return content;
}

// ----------------------------------------------------------------------------
// Section — title + eyebrow + body wrapper with consistent rhythm
// ----------------------------------------------------------------------------
export function TrustSection({
  eyebrow,
  title,
  highlightWord,
  body,
  children,
  tone = "default", // "default" | "amber" | "white" | "transparent"
  testid,
}) {
  const tones = {
    default: "bg-white border-y border-emerald-100",
    amber: "bg-gradient-to-br from-amber-50/70 via-white to-emerald-50/30",
    white: "bg-white",
    transparent: "",
  };
  return (
    <section className={tones[tone] || tones.default} data-testid={testid}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {eyebrow && (
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light leading-tight mt-2 max-w-3xl">
            {renderHighlighted(title, highlightWord)}
          </h2>
        )}
        {body && (
          <p className="mt-3 text-base text-emerald-900/75 max-w-2xl leading-relaxed">
            {body}
          </p>
        )}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Icon card grid (used by 'meet your mentors', 'science principles', etc.)
// ----------------------------------------------------------------------------
export function IconCardGrid({ cards, cols = 3, testid }) {
  const colMap = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  };
  return (
    <div className={`grid ${colMap[cols] || colMap[3]} gap-4`} data-testid={testid}>
      {cards.map((c, idx) => (
        <article
          key={c.title + idx}
          className="rounded-2xl border border-emerald-100 bg-white p-6 hover:border-emerald-300 hover:shadow-sm transition"
          data-testid={c.testid || `icon-card-${idx}`}
        >
          {c.icon && (
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 mb-3">
              <c.icon className="w-4 h-4" strokeWidth={1.6} />
            </span>
          )}
          {c.eyebrow && (
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-1">
              {c.eyebrow}
            </p>
          )}
          <h3 className="font-display text-lg text-emerald-950 font-medium leading-snug">{c.title}</h3>
          {c.body && <p className="text-[13.5px] text-emerald-900/75 mt-2 leading-relaxed">{c.body}</p>}
          {c.cta && (
            <Link to={c.cta.to} className="inline-flex items-center mt-3 text-emerald-700 hover:text-emerald-800 text-sm" data-testid={c.cta.testid}>
              {c.cta.label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Link>
          )}
        </article>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Numbered steps (used by /how-it-works)
// ----------------------------------------------------------------------------
export function StepList({ steps, testid }) {
  return (
    <ol className="space-y-4" data-testid={testid}>
      {steps.map((s, idx) => (
        <li
          key={s.title + idx}
          className="rounded-3xl border border-emerald-100 bg-white p-5 sm:p-7 flex gap-5 items-start"
          data-testid={`step-${idx + 1}`}
        >
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-emerald-700 text-white font-display text-lg font-light shrink-0">
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg sm:text-xl text-emerald-950 font-medium">{s.title}</h3>
            {s.body && <p className="text-sm text-emerald-900/75 mt-1.5 leading-relaxed">{s.body}</p>}
            {s.note && <p className="text-[12px] text-amber-800 mt-2 italic">{s.note}</p>}
          </div>
          {s.icon && (
            <span className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 shrink-0">
              <s.icon className="w-4 h-4" strokeWidth={1.6} />
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

// ----------------------------------------------------------------------------
// "Still unsure?" / closing CTA block
// ----------------------------------------------------------------------------
export function ClosingCta({
  eyebrow = "Real guidance",
  title = "Not sure where to start?",
  body = "We'd rather guide you well than rush a sale. Tell us a little — we'll point you to the right next step.",
  testid = "trust-closing-cta",
}) {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <div
        className="rounded-3xl border border-amber-200/60 bg-gradient-to-br from-amber-50/70 via-white to-emerald-50/30 p-7 sm:p-12 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-center"
        data-testid={testid}
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-semibold">{eyebrow}</p>
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
            {title}
          </h2>
          <p className="text-sm text-emerald-900/75 mt-3 max-w-xl leading-relaxed">{body}</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:flex-col">
          <Link to="/questionnaire" data-testid="closing-cta-assessment">
            <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-5">
              <Sparkles className="w-4 h-4 mr-1.5" /> Take free assessment
            </Button>
          </Link>
          <Link to="/mentor-support" data-testid="closing-cta-mentor">
            <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 px-5 py-5">
              <HeartHandshake className="w-4 h-4 mr-1.5" /> Talk to a mentor
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Reassurance ribbon — three icon+title pillars
// ----------------------------------------------------------------------------
export function ReassuranceRibbon({ items, testid = "reassurance-ribbon" }) {
  return (
    <section className="bg-emerald-50/30 border-y border-emerald-100" data-testid={testid}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
        {items.map((it, idx) => (
          <div key={it.title + idx} className="rounded-2xl bg-white border border-emerald-100 p-4">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 mb-2">
              <it.icon className="w-4 h-4" strokeWidth={1.6} />
            </span>
            <h4 className="font-display text-sm text-emerald-950 font-medium">{it.title}</h4>
            {it.body && <p className="text-[12px] text-emerald-900/70 mt-1 leading-relaxed">{it.body}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Inline WhatsApp link (with config-driven number)
// ----------------------------------------------------------------------------
export function WhatsAppInline({ number = "447424225333", text, testid, children = "Chat on WhatsApp" }) {
  const url = `https://wa.me/${number}?text=${encodeURIComponent(text || "Hi Feel Greats team!")}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center text-emerald-700 hover:text-emerald-800 font-semibold"
      data-testid={testid}
    >
      <MessageCircle className="w-4 h-4 mr-1.5" /> {children}
    </a>
  );
}
